(() => {
  if (customElements.get('alleycat-related-collection')) return;

  class AlleycatRelatedCollection extends HTMLElement {
    connectedCallback() {
      if (this.ownerDocument !== document) return;
      if (this.closest('quick-add-modal')) return;
      this.list = this.querySelector('.alleycat-related-collection__list');
      if (!this.list) return;

      this.generation = (this.generation || 0) + 1;
      this.loading = false;
      this.stopped = false;
      this.retryAttempts = 0;
      this.retryBlocked = false;
      this.retryTimer = null;
      this.seenUrls = new Set();
      this.productIds = new Set();

      for (const item of this.list.querySelectorAll('li[data-product-id]')) {
        const id = item.dataset.productId;
        if (this.productIds.has(id)) item.remove();
        else this.productIds.add(id);
      }

      this.onScroll = () => {
        this.resumeRetries();
        this.scheduleCheck();
      };
      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('online', this.onScroll);
      this.resizeObserver = new ResizeObserver(() => this.scheduleCheck());
      this.resizeObserver.observe(this.list);
      this.nearViewport = typeof IntersectionObserver === 'undefined';
      if (!this.nearViewport) {
        // The slider itself can be hidden until a later page contains an available piece.
        const visibilityTarget = this.closest('.alleycat-product-page__after-stage') || this;
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          const wasNearViewport = this.nearViewport;
          this.nearViewport = entry.isIntersecting;
          if (this.nearViewport) {
            if (!wasNearViewport) this.resumeRetries();
            this.scheduleCheck();
          }
        }, { rootMargin: '600px 0px' });
        this.visibilityObserver.observe(visibilityTarget);
      }
      this.scheduleCheck();
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.requestController?.abort();
      this.requestController = null;
      this.list?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('online', this.onScroll);
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      cancelAnimationFrame(this.checkFrame);
      this.checkFrame = null;
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    scheduleCheck() {
      if (this.checkFrame) return;
      this.checkFrame = requestAnimationFrame(() => {
        this.checkFrame = null;
        this.checkForMore();
      });
    }

    checkForMore() {
      if (!this.isConnected || !this.list || this.loading || this.stopped || !this.nearViewport ||
        this.retryTimer || this.retryBlocked) return;
      if (!this.dataset.nextUrl) {
        if (!this.productIds.size) this.remove();
        return;
      }

      if (this.hidden || (this.list.clientWidth &&
        this.list.scrollWidth - this.list.clientWidth - this.list.scrollLeft <= Math.max(300, this.list.clientWidth))) {
        this.loadMore();
      }
    }

    async loadMore() {
      let url;
      try {
        url = new URL(this.dataset.nextUrl, window.location.href);
        if (url.origin !== window.location.origin || !this.dataset.sectionId) throw new Error('Invalid section URL');
        const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewThemeId && !url.searchParams.has('preview_theme_id')) {
          url.searchParams.set('preview_theme_id', previewThemeId);
        }
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.hash = '';
        url.searchParams.sort();
        if (this.seenUrls.has(url.href)) throw new Error('Repeated section URL');
      } catch {
        this.stopLoading();
        return;
      }

      this.loading = true;
      const generation = this.generation;
      const controller = new AbortController();
      this.requestController = controller;

      try {
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Section request failed');
        const markup = await response.text();
        if (generation !== this.generation || !this.isConnected) return;
        const documentFromResponse = new DOMParser().parseFromString(markup, 'text/html');

        const nextSlider = [...documentFromResponse.querySelectorAll('alleycat-related-collection')]
          .find((slider) => slider.dataset.sectionId === this.dataset.sectionId);
        if (!nextSlider) {
          this.stopLoading();
          return;
        }

        const nextList = nextSlider.querySelector('.alleycat-related-collection__list');
        if (!nextList) {
          this.stopLoading();
          return;
        }

        this.seenUrls.add(url.href);
        this.retryAttempts = 0;
        const fragment = document.createDocumentFragment();
        for (const item of nextList.children) {
          const id = item.dataset.productId;
          if (item.tagName !== 'LI' || !id || this.productIds.has(id)) continue;
          this.productIds.add(id);
          fragment.append(document.importNode(item, true));
        }
        this.list.append(fragment);
        if (this.productIds.size) this.hidden = false;
        this.dataset.nextUrl = nextSlider.dataset.nextUrl || '';
        if (!this.dataset.nextUrl && !this.productIds.size) this.remove();
      } catch (error) {
        if (generation === this.generation && error.name !== 'AbortError') this.scheduleRetry();
      } finally {
        if (generation === this.generation) {
          this.requestController = null;
          this.loading = false;
          this.scheduleCheck();
        }
      }
    }

    scheduleRetry() {
      // Keep the next page after a temporary network failure, but avoid an endless request loop.
      const delays = [600, 1800];
      if (this.retryAttempts >= delays.length) {
        this.retryBlocked = true;
        return;
      }
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.scheduleCheck();
      }, delays[this.retryAttempts++]);
    }

    resumeRetries() {
      if (!this.retryBlocked) return;
      this.retryBlocked = false;
      this.retryAttempts = 0;
    }

    stopLoading() {
      this.stopped = true;
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.dataset.nextUrl = '';
      if (!this.productIds.size) this.remove();
    }
  }

  customElements.define('alleycat-related-collection', AlleycatRelatedCollection);
})();
